import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bomb, Flame, LayoutDashboard, LogOut, Music2, ShoppingBag, Sparkles, Trophy, Users, GraduationCap } from "lucide-react";
import { StoreFlow } from "../components/store/StoreFlow";
import { Ticker } from "../components/table/Ticker";
import { DailyStreakModal } from "../components/lobby/DailyStreakModal";
import { WelcomeModal } from "../components/lobby/WelcomeModal";
import { MttList } from "../components/lobby/MttList";
import { ClubRooms } from "../components/lobby/ClubRooms";
import { useAuth } from "../context/AuthContext";
import { api, C, fmtChips } from "../lib/api";
import { loadPendingRequests } from "../lib/friends";
import { JackpotBox, fmtCents } from "../components/JackpotBox";
import { MaintenanceBanner, useMaintenance } from "../components/MaintenanceBanner";
import { FormatFilters, GameFilters } from "../components/lobby/GameFilters";
import { AssetImage } from "../components/AssetImage";
import { getAsset } from "../assets/assetManifest";
import { music, MUSIC_CREDIT } from "../lib/music";
import { toast } from "sonner";
import { JoinButton } from "../components/JoinDialog";
import { GrinderTopThree } from "../components/lobby/GrinderTopThree";

export default function Lobby() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tables, setTables] = useState([]);
  const [store, setStore] = useState(false);
  const [format, setFormat] = useState("CASH"); // Cash · Sit N Go · MTT
  const [filter, setFilter] = useState("ALL");
  const [mttGames, setMttGames] = useState([]);
  const inFormat = tables.filter((t) => (format === "SNG" ? t.format === "SNG" : t.format !== "SNG"));
  const games = format === "MTT" ? mttGames : inFormat.map((t) => t.game);
  const visible = format === "MTT" ? [] : inFormat.filter((t) => filter === "ALL" || t.game === filter);
  const pickFormat = (f) => { setFormat(f); setFilter("ALL"); };
  useEffect(() => { music.start(1); return () => music.stop(); }, []);
  // The Daily Streak: pops once a day, when today's reward is still unclaimed (a second login the same day sees nothing)
  const [streak, setStreak] = useState(null);
  const [jackpot, setJackpot] = useState(null); // {top, pools}
  const maint = useMaintenance();
  const poolFor = (t) => (t.jackpot_pool ? jackpot?.pools?.find((p) => p.key === t.jackpot_pool) : null); // only the rooms in the jackpot carry a pool
  useEffect(() => {
    const load = () => api.get(C.ApiRoutes.JACKPOT).then((r) => setJackpot(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);
  // JOIN: the server picks the copy with a seat (cash) / still registering (SNG), or opens a fresh one; watching a full game falls back to its lobby copy
  const joinGame = async (t) => {
    try {
      const { data } = await api.post(C.ApiRoutes.TABLE_JOIN.replace("{table_id}", t.id));
      if (data.reason === "NEW_TABLE") toast.success(t.tournament ? "A new Sit N Go has been opened for you" : "A new table has been opened for you");
      nav(`/table/${data.table_id}`);
    } catch { nav(`/table/${t.copy_id || t.id}`); }
  };
  const [showStreak, setShowStreak] = useState(false);
  // A brand-new player sees the welcome window first (what they were given); the streak waits until it is closed
  const [welcome, setWelcome] = useState(() => {
    const raw = sessionStorage.getItem("pfp_welcome");
    if (!raw) return null;
    sessionStorage.removeItem("pfp_welcome");
    try { return JSON.parse(raw); } catch { return null; }
  });
  const [streakReady, setStreakReady] = useState(false);
  useEffect(() => { api.get(C.ApiRoutes.STREAK).then((r) => { setStreak(r.data); if (r.data.claimable) setStreakReady(true); }).catch(() => {}); }, []);
  useEffect(() => { if (streakReady && !welcome) setShowStreak(true); }, [streakReady, welcome]);
  const closeStreak = useCallback(() => setShowStreak(false), []);
  const onClaimed = useCallback(() => { window.dispatchEvent(new Event("pfp-balances")); api.get(C.ApiRoutes.STREAK).then((r) => setStreak(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const loadTables = () => api.get(C.ApiRoutes.TABLES).then((r) => setTables(r.data.filter((t) => !t.club))).catch(() => {});  // club ring games live in their room
    loadTables();
    const id = setInterval(loadTables, 30000); // live seat counts; doubles as the "online" heartbeat for player info cards
    return () => clearInterval(id);
  }, []);
  useEffect(() => { loadPendingRequests(); }, []);

  return (
    <div className="min-h-screen px-8 py-8 md:px-16 relative overflow-hidden" style={{ backgroundImage: `url(${getAsset("bg_lobby_main").path})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed", backgroundColor: "#000" }}>
      {/* 3D carpet: tiled texture on a perspective plane pinned to the floor, fading into the room */}
      <div aria-hidden data-testid="lobby-carpet" className="pointer-events-none fixed inset-x-0 bottom-0 h-[58vh]" style={{ perspective: "700px", perspectiveOrigin: "50% 0%", maskImage: "linear-gradient(to bottom, transparent 0%, #000 45%)", WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, #000 45%)" }}>
        <div className="absolute left-[-40%] right-[-40%] bottom-[-10%] h-[160%]" style={{ backgroundImage: `url(${getAsset("tex_carpet").path})`, backgroundSize: "420px 420px", transform: "rotateX(62deg)", transformOrigin: "50% 100%", opacity: 0.9, filter: "brightness(0.85)" }} />
      </div>
      <div className="-mx-8 md:-mx-16 -mt-8 mb-6 relative z-20" data-testid="lobby-ticker"><Ticker /></div>
      <div className="relative">
      <MaintenanceBanner state={maint} />
      {jackpot?.top && <div className="flex justify-center mb-6" data-testid="lobby-jackpot-row"><JackpotBox cents={jackpot.top.amount_cents} label={jackpot.top.stakes} testId="lobby-jackpot" /></div>}
      <header className="flex items-center justify-between mb-12">
        <div>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.35em] gold-text"><AssetImage id="logo_emblem" height={44} data-testid="lobby-logo" /> Players Room</div>
          <h1 className="text-2xl md:text-3xl mt-2" data-testid="lobby-greeting">Welcome back, {user.display_name}</h1>
        </div>
        <div className="flex items-center gap-3">
          {user.guest ? (
            <JoinButton testId="lobby-join-button" label="JOIN · 50,000 CHIPS" className="px-5 py-2 tracking-[0.2em]" />
          ) : (
            <button data-testid="lobby-store-button" onClick={() => setStore(true)} className="btn-gold rounded-full px-5 py-2 text-xs font-display tracking-[0.2em] btn-lift flex items-center gap-2"><ShoppingBag className="w-3.5 h-3.5" /> STORE</button>
          )}
          <button data-testid="leaderboard-link" onClick={() => nav("/leaderboard")} title="Weekly Grinder & Monthly Bankroll Builder" className="gold-card rounded-full px-4 py-2 text-xs hover:border-amber-400/80 btn-lift flex items-center gap-2"><Trophy className="w-3 h-3 text-amber-200" /> Leaderboard</button>
          <button data-testid="rules-link" onClick={() => nav("/rules")} title="How every game is played" className="gold-card rounded-full px-4 py-2 text-xs hover:border-amber-400/80 btn-lift flex items-center gap-2"><GraduationCap className="w-3 h-3 text-amber-200" /> Rules</button>
          {streak && (
            <button data-testid="streak-badge" onClick={() => setShowStreak(true)} title="The Daily Streak" className="gold-card rounded-full px-4 py-2 text-xs hover:border-amber-400/80 btn-lift flex items-center gap-2">
              <Flame className={`w-3 h-3 ${streak.claimable ? "text-amber-400 animate-pulse" : "text-amber-200"}`} /> Day {streak.claimable ? streak.day : streak.claimed_through || streak.day}{streak.claimable ? " · claim" : ""}
            </button>
          )}
          {C.STAFF_ROLES.includes(user.role) && (
            <button data-testid="admin-link" onClick={() => nav("/admin")} className="btn-gold rounded-full px-4 py-2 text-xs font-display hover:brightness-110 btn-lift flex items-center gap-2"><LayoutDashboard className="w-3 h-3" /> Dashboard</button>
          )}
          <button data-testid="logout-button" onClick={async () => { await logout(); nav("/"); }} className="gold-card rounded-full px-4 py-2 text-xs hover:border-amber-400/80 btn-lift flex items-center gap-2"><LogOut className="w-3 h-3" /> Sign out</button>
        </div>
      </header>
      <GrinderTopThree />
      {store && <StoreFlow origin="lobby" after="stay" onClose={() => setStore(false)}
        onPurchased={() => window.dispatchEvent(new Event("pfp-balances"))} />}

      {/* Chips, gold, VIP and friends live in the MENU now — this column is the club rooms */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-3 flex flex-col gap-6">
          <ClubRooms />
        </section>

        <section className="lg:col-span-9 flex flex-col gap-6">
          <div className="gold-card rounded-2xl p-6 rise" style={{ animationDelay: "120ms" }}>
            <div className="flex flex-col gap-4 mb-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <h2 className="text-base md:text-lg gold-text uppercase tracking-[0.2em]">Available games</h2>
                <FormatFilters value={format} onChange={pickFormat} />
              </div>
              <GameFilters value={filter} games={games} onChange={setFilter} />
            </div>
            {format === "MTT" && <MttList bare game={filter} onGames={setMttGames}
              onBalances={() => window.dispatchEvent(new Event("pfp-balances"))} />}
            <div className="flex flex-col gap-3">
              {visible.map((t) => (
                <div key={t.id} data-testid={`table-row-${t.id}`} className="gold-row flex items-center justify-between rounded-xl px-5 py-4">
                  <div>
                    <div className="font-display text-sm text-amber-50 flex items-center gap-2">{t.name}
                      {t.bomb_pot && <span data-testid={`table-bomb-${t.id}`} title="Double Board Bomb Pot: three of a suit on the flop → next hand everyone antes 2 BB, no pre-flop betting, two boards, half-pot max" className="inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-gradient-to-b from-orange-600/80 to-red-800/80 px-2 py-0.5 text-[9px] tracking-[0.2em] text-amber-100"><Bomb className="w-3 h-3" /> BOMB POT</span>}
                    </div>
                    <div className="text-xs text-slate-400 mt-1 font-mono">{C.GAME_RULES[t.game]?.label || t.game} · {t.tournament
                      ? <span data-testid={`sng-info-${t.id}`}>Sit N Go · buy-in {fmtChips(t.tournament.buy_in)} + {fmtChips(t.tournament.fee)} fee · {t.tournament.status === "REGISTERING" ? `registering ${t.tournament.registered}/${t.tournament.seats}` : `${t.tournament.status.toLowerCase()} · join opens a new one`}</span>
                      : <>{t.stakes} · buy-in {fmtChips(t.min_buyin)}–{fmtChips(t.max_buyin)}</>}</div>
                  </div>
                  <div className="flex items-center gap-5">
                    <span className="flex items-center gap-1 text-sm text-slate-300" data-testid={`table-seated-${t.id}`} title={t.instances > 1 ? `${t.instances} tables of this game are running` : undefined}>
                      <Users className="w-4 h-4 gold-text" /> {t.instances > 1 ? <>{t.seated_total} players <span className="text-xs text-amber-200/80">· {t.instances} tables</span></> : <>{t.seated}/{t.max_seats}</>}
                    </span>
                    {!t.tournament && poolFor(t) && <span data-testid={`table-jackpot-${t.id}`} title="Jackpot pool this room plays for" className="hidden md:flex items-center gap-1 text-[11px] font-mono text-amber-200/90"><Sparkles className="w-3 h-3 text-amber-400" /> {fmtCents(poolFor(t).amount_cents)}</span>}
                    <button data-testid={`join-table-${t.id}`} onClick={() => joinGame(t)} className="btn-gold rounded-full px-6 py-2 text-sm font-display btn-lift">Join</button>
                  </div>
                </div>
              ))}
              {tables.length === 0 && <div className="text-sm text-slate-400" data-testid="tables-loading">Loading tables…</div>}
              {tables.length > 0 && format !== "MTT" && visible.length === 0 && <div className="text-sm text-slate-400 py-6 text-center" data-testid="tables-empty">{format === "SNG" ? "No Sit N Go tables open right now." : "No cash games in this category right now."}</div>}
            </div>
          </div>
        </section>
      </div>
      </div>
      <div data-testid="lobby-copyright" className="fixed bottom-2 left-1/2 -translate-x-1/2 z-10 text-[10px] text-slate-400/70 whitespace-nowrap">
        © {new Date().getFullYear()} Players Room. All rights reserved. · 18+ · Chips are not redeemable
      </div>
      <footer data-testid="music-credit" className="fixed bottom-2 right-4 z-10 flex items-center gap-1.5 text-[10px] text-slate-400/70 hover:text-slate-300 transition-colors">
        <Music2 className="w-3 h-3" /> <span>{MUSIC_CREDIT}</span>
      </footer>
      {welcome && <WelcomeModal welcome={welcome} onClose={() => { setWelcome(null); onClaimed(); }} />}
      {showStreak && <DailyStreakModal onClose={closeStreak} onClaimed={onClaimed} />}
    </div>
  );
}
